import { useQuery } from "@apollo/client";
import { STATE_NAMES } from "encamp-shared/src/constants/states";
import { convertStateNameToAbbreviation } from "encamp-shared/src/utils/states";
import { WorkflowManagerConfigType } from "generated-graphql/graphql";
import { sortBy } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import { WORKFLOW_MANAGER_CONFIG_RULES_QUERY } from "../Rules";
import {
  isTenantRelatedEntity,
  RuleType,
  StateOption,
  TenantOption,
} from "./types";

/**
 * Form state for the Add Rule modal
 */
export interface AddRuleFormState {
  selectedRuleType: RuleType | null;
  value: string;
  notes: string;
  stateInputValue: string;
  needsCandidate: boolean;
  notImplemented: boolean;
  tenantForStateValue: string;
  selectedTenant: TenantOption | null;
  tenantSearchInput: string;
}

const initialFormState: AddRuleFormState = {
  selectedRuleType: null,
  value: "",
  notes: "",
  stateInputValue: "",
  needsCandidate: false,
  notImplemented: false,
  tenantForStateValue: "",
  selectedTenant: null,
  tenantSearchInput: "",
};

/**
 * Hook to manage form state for the Add Rule modal
 */
export function useAddRuleForm() {
  const [formState, setFormState] = useState<AddRuleFormState>(initialFormState);
  const [debouncedTenantSearch] = useDebounce(formState.tenantSearchInput, 150);

  // Query variables for tenant search
  const tenantSearchVariables = useMemo(
    () => ({
      search: debouncedTenantSearch,
      pageSize: 20,
      sort: [{ field: "name", sort: "asc" as const }],
    }),
    [debouncedTenantSearch]
  );

  // Query existing rules to filter out already-used states
  const { data: existingRulesData } = useQuery(
    WORKFLOW_MANAGER_CONFIG_RULES_QUERY,
    {
      variables: { pageSize: 1000 },
      fetchPolicy: "cache-first",
    }
  );

  // Extract state codes that already have rules for each type
  const existingStatesByType = useMemo(() => {
    const rules = existingRulesData?.workflowManagerConfigRules?.items ?? [];
    const result: Record<string, Set<string>> = {
      [WorkflowManagerConfigType.AllowState]: new Set(),
      [WorkflowManagerConfigType.AllowStateRevision]: new Set(),
    };

    for (const rule of rules) {
      if (
        rule.type === WorkflowManagerConfigType.AllowState ||
        rule.type === WorkflowManagerConfigType.AllowStateRevision
      ) {
        // Extract just the state code (handle cases like "TX_NEEDS_CANDIDATE")
        const stateCode = rule.value.split("_")[0];
        result[rule.type].add(stateCode);
      }
    }

    return result;
  }, [existingRulesData]);

  // Build state options, filtering out states that already have rules for the selected type
  const stateOptions = useMemo((): StateOption[] => {
    const allStates = sortBy(Object.entries(STATE_NAMES), 1).map(
      ([abbr, name]) => ({
        value: abbr,
        label: `${name} (${abbr.toUpperCase()})`,
      })
    );

    // Only filter if selecting an Allow State type
    if (
      formState.selectedRuleType?.configType ===
        WorkflowManagerConfigType.AllowState ||
      formState.selectedRuleType?.configType ===
        WorkflowManagerConfigType.AllowStateRevision
    ) {
      const existingStates =
        existingStatesByType[formState.selectedRuleType.configType] ?? new Set();
      return allStates.filter((state) => !existingStates.has(state.value));
    }

    return allStates;
  }, [formState.selectedRuleType, existingStatesByType]);

  // Derived state
  const entityType = formState.selectedRuleType?.entity;

  // Normalize state input: extract abbreviation from various formats
  const extractStateCode = useCallback((input: string): string => {
    const trimmed = input.trim();
    const upperTrimmed = trimmed.toUpperCase();

    // Check if it's already a 2-letter code
    if (/^[A-Z]{2}$/.test(upperTrimmed)) {
      return upperTrimmed;
    }

    // Try to extract from format "State Name (ABBR)" or "(ABBR)"
    const parenMatch = trimmed.match(/\(([A-Z]{2})\)/i);
    if (parenMatch) {
      return parenMatch[1].toUpperCase();
    }

    // Try to convert state name to abbreviation
    const abbreviation = convertStateNameToAbbreviation(trimmed);
    if (abbreviation) {
      return abbreviation;
    }

    return upperTrimmed;
  }, []);

  // Compute final value for submission
  const computeFinalValue = useCallback((): string => {
    const { selectedRuleType, value, needsCandidate, notImplemented, tenantForStateValue, selectedTenant } = formState;
    
    if (!selectedRuleType) return "";

    const isStateType =
      selectedRuleType.entity === "state" ||
      selectedRuleType.entity === "stateRevision";

    if (isStateType) {
      const stateCode = extractStateCode(value.trim());

      if (
        selectedRuleType.entity === "stateRevision" &&
        selectedRuleType.action === "allow"
      ) {
        if (needsCandidate) {
          return `${stateCode}_NEEDS_CANDIDATE`;
        } else if (notImplemented) {
          return `${stateCode}_NOT_IMPLEMENTED`;
        }
      }
      return stateCode;
    } else if (selectedRuleType.entity === "tenantForState") {
      const tenantId = selectedTenant?.id ?? "";
      const stateCode = extractStateCode(tenantForStateValue?.trim() ?? "");
      return `${tenantId}:${stateCode}`;
    } else if (selectedRuleType.entity === "tenant") {
      return selectedTenant?.id ?? "";
    } else {
      return value.trim();
    }
  }, [formState, extractStateCode]);

  // Helper to compute auto-populated notes for tenant rules
  const computeAutoNotes = useCallback(
    (
      tenant: TenantOption | null,
      stateValue: string,
      entity: string | undefined
    ): string => {
      if (entity === "tenantForState" && tenant && stateValue) {
        const stateName =
          STATE_NAMES[stateValue as keyof typeof STATE_NAMES] ?? stateValue;
        return `${tenant.name}: ${stateName}`;
      }
      if (entity === "tenant" && tenant) {
        return tenant.name;
      }
      return "";
    },
    []
  );

  // Actions
  const reset = useCallback(() => {
    setFormState(initialFormState);
  }, []);

  const setField = useCallback(
    <K extends keyof AddRuleFormState>(field: K, value: AddRuleFormState[K]) => {
      setFormState((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const setSelectedRuleType = useCallback((ruleType: RuleType | null) => {
    // Reset all fields when rule type changes
    setFormState({
      ...initialFormState,
      selectedRuleType: ruleType,
    });
  }, []);

  const setValue = useCallback((value: string) => {
    setFormState((prev) => ({ ...prev, value }));
  }, []);

  const setNotes = useCallback((notes: string) => {
    setFormState((prev) => ({ ...prev, notes }));
  }, []);

  const setStateInputValue = useCallback((stateInputValue: string) => {
    setFormState((prev) => ({
      ...prev,
      stateInputValue,
      value: stateInputValue,
    }));
  }, []);

  const setNeedsCandidate = useCallback((needsCandidate: boolean) => {
    setFormState((prev) => ({
      ...prev,
      needsCandidate,
      notImplemented: needsCandidate ? false : prev.notImplemented,
    }));
  }, []);

  const setNotImplemented = useCallback((notImplemented: boolean) => {
    setFormState((prev) => ({
      ...prev,
      notImplemented,
      needsCandidate: notImplemented ? false : prev.needsCandidate,
    }));
  }, []);

  const setTenantForStateValue = useCallback((tenantForStateValue: string) => {
    setFormState((prev) => {
      const newNotes = computeAutoNotes(
        prev.selectedTenant,
        tenantForStateValue,
        prev.selectedRuleType?.entity
      );
      return { ...prev, tenantForStateValue, notes: newNotes };
    });
  }, [computeAutoNotes]);

  const setSelectedTenant = useCallback(
    (tenant: TenantOption | null) => {
      setFormState((prev) => {
        const newNotes = computeAutoNotes(
          tenant,
          prev.tenantForStateValue,
          prev.selectedRuleType?.entity
        );
        return { ...prev, selectedTenant: tenant, notes: newNotes };
      });
    },
    [computeAutoNotes]
  );

  const setTenantSearchInput = useCallback((tenantSearchInput: string) => {
    setFormState((prev) => ({ ...prev, tenantSearchInput }));
  }, []);

  // Validation
  const isValid = useMemo(() => {
    const { selectedRuleType, value, selectedTenant, tenantForStateValue } = formState;
    
    if (!selectedRuleType) return false;

    const isTenantRule = isTenantRelatedEntity(selectedRuleType.entity);

    if (isTenantRule) {
      if (!selectedTenant) return false;
      if (selectedRuleType.entity === "tenantForState" && !tenantForStateValue.trim()) {
        return false;
      }
    } else if (!value.trim()) {
      return false;
    }

    return true;
  }, [formState]);

  // Derived flags
  const isStateRevisionAllow =
    formState.selectedRuleType?.action === "allow" &&
    formState.selectedRuleType?.entity === "stateRevision";

  return {
    formState,
    entityType,
    stateOptions,
    tenantSearchVariables,
    isValid,
    isStateRevisionAllow,
    // Actions
    reset,
    setField,
    setSelectedRuleType,
    setValue,
    setNotes,
    setStateInputValue,
    setNeedsCandidate,
    setNotImplemented,
    setTenantForStateValue,
    setSelectedTenant,
    setTenantSearchInput,
    computeFinalValue,
  };
}
